import { z } from 'zod';
import {
    HotspotImageSchema,
    HotspotImageWithThumbSchema,
    ImageSchema,
} from '../../../common/schemas/imageZodSchemas';
import { transformAndFilterValidEdges } from '../../../common/tools/transformEdgesUtils';
import { transformAndFilterValidItems } from '../../../common/tools/transformUtils';
import { BaseResourceLibraryItemSchema } from './resourceLibraryBaseZodSchemas';

const LinkSchema = z
    .object({
        path: z.string(),
        target: z.string().nullable(),
        anchor: z.string().nullable(),
        parameters: z.string().nullable(),
    })
    .passthrough() // Allow extra fields we don't care about
    .describe('ResourceLibrarySitePageLinkSchema');

export const ResourceLibrarySitePageTemplateSchema = z
    .enum([
        'ApplicationArticle',
        'Generic',
        'SimpleTextVideo',
        'ImageGalleryArticle',
        'MixedMediaArticle',
    ])
    .describe('ResourceLibrarySitePageTemplateSchema');

// Section blocks

const ResourceLibrarySitePageHeadingSectionSchema = z
    .object({
        __typename: z.literal('fieldcollection_HeadingSection'),
        level: z.number().finite(),
        mergeWithPrevious: z.boolean(),
        title: z.string().min(1),
    })
    .describe('ResourceLibrarySitePageHeadingSectionSchema');

const ResourceLibrarySitePagePlainTextSectionSchema = z
    .object({
        __typename: z.literal('fieldcollection_PlainTextSection'),
        level: z.number(),
        mergeWithPrevious: z.boolean(),
        title: z.string().nullable(),
        content: z.string().min(1),
    })
    .describe('ResourceLibrarySitePagePlainTextSectionSchema');

const ResourceLibrarySitePageRichTextSectionSchema = z
    .object({
        __typename: z.literal('fieldcollection_RichTextSection'),
        level: z.number(),
        mergeWithPrevious: z.boolean(),
        title: z.string().nullable(),
        content: z.string().min(1),
    })
    .describe('ResourceLibrarySitePageRichTextSectionSchema');

const ResourceLibrarySitePagePlainTextImageSectionSchema = z
    .object({
        __typename: z.literal('fieldcollection_PlainTextImageSection'),
        level: z.number(),
        mergeWithPrevious: z.boolean(),
        title: z.string().nullable(),
        content: z.string().min(1),
        image: HotspotImageSchema,
        imageLink: LinkSchema.nullable(),
        imageTitle: z.string().nullable(),
        imageCaption: z.string().nullable(),
    })
    .describe('ResourceLibrarySitePagePlainTextImageSectionSchema');

const ResourceLibrarySitePageRichTextImageSectionSchema = z
    .object({
        __typename: z.literal('fieldcollection_RichTextImageSection'),
        level: z.number(),
        mergeWithPrevious: z.boolean(),
        title: z.string().nullable(),
        content: z.string().min(1),
        image: HotspotImageSchema,
        imageLink: LinkSchema.nullable(),
        imageTitle: z.string().nullable(),
        imageCaption: z.string().nullable(),
    })
    .describe('ResourceLibrarySitePageRichTextImageSectionSchema');

// Content blocks

const ResourceLibrarySitePageImageBlockSchema = z
    .object({
        __typename: z.literal('fieldcollection_ImageBlock'),
        image: HotspotImageWithThumbSchema,
        imageTitle: z.string().nullable(),
        imageCaption: z.string().nullable(),
        imageLink: LinkSchema.nullable(),
    })
    .describe('ResourceLibrarySitePageImageBlockSchema');

const VideoBlockVideoAssetSchema = z
    .object({
        __typename: z.literal('asset'),
        id: z.string().min(1),
        fullpath: z.string().min(1),
        mimetype: z.string().min(1),
        type: z.string().min(1),
    })
    .describe('VideoBlockVideoAssetSchema');

const VideoBlockVideoExternalSchema = z
    .object({
        __typename: z.literal('VideoDataDescriptor'),
        videoId: z.string().min(1),
    })
    .describe('VideoBlockVideoExternalSchema');

const VideoBlockVideoDataSchema = z
    .union([VideoBlockVideoAssetSchema, VideoBlockVideoExternalSchema])
    .describe('VideoBlockVideoDataSchema');

const ResourceLibrarySitePageVideoBlockSchema = z
    .object({
        __typename: z.literal('fieldcollection_VideoBlock'),
        video: z
            .object({
                type: z.string().min(1),
                data: VideoBlockVideoDataSchema,
            })
            .describe('ResourceLibrarySitePageVideoBlockVideoSchema'),
        ariaLabel: z.string().min(1),
    })
    .describe('ResourceLibrarySitePageVideoBlockSchema');

export const ResourceLibrarySitePageInternalLinkBlockSchema = z
    .object({
        __typename: z.literal('fieldcollection_InternalLinkBlock'),
        openInNewTab: z.boolean(),
        linkText: z.string(),
        linkRoute: z.string(),
    })
    .describe('ResourceLibrarySitePageInternalLinkBlockSchema');

export const ResourceLibrarySitePageTableBlockSchema = z
    .object({
        __typename: z.literal('fieldcollection_TableBlock'),
        firstRowIsHeader: z.boolean(),
        firstColumnIsHeader: z.boolean(),
        table: z.string().min(1),
        caption: z.string().nullable(),
    })
    .describe('ResourceLibrarySitePageTableBlockSchema');

export const ResourceLibrarySitePageRichTableBlockSchema = z
    .object({
        __typename: z.literal('fieldcollection_RichTableBlock'),
        tableHtml: z.string().min(1),
        caption: z.string().nullable(),
    })
    .describe('ResourceLibrarySitePageRichTableBlockSchema');

export const ResourceLibrarySitePageSectionSchema = z
    .union([
        ResourceLibrarySitePageHeadingSectionSchema,
        ResourceLibrarySitePagePlainTextSectionSchema,
        ResourceLibrarySitePageRichTextSectionSchema,
        ResourceLibrarySitePagePlainTextImageSectionSchema,
        ResourceLibrarySitePageRichTextImageSectionSchema,
        ResourceLibrarySitePageImageBlockSchema,
        ResourceLibrarySitePageVideoBlockSchema,
        ResourceLibrarySitePageInternalLinkBlockSchema,
        ResourceLibrarySitePageTableBlockSchema,
        ResourceLibrarySitePageRichTableBlockSchema,
    ])
    .describe('ResourceLibrarySitePageSectionSchema');

const NavigationNodeSchema = z
    .object({
        id: z.string(),
    })
    .describe('ResourceLibrarySitePageNavigationNodeSchema');

export const ResourceLibrarySitePageBaseItemSchema = BaseResourceLibraryItemSchema.extend({
    template: ResourceLibrarySitePageTemplateSchema,
    coverImage: ImageSchema.nullable(),
    lastModificationDate: z.string().nullable(),
}).describe('ResourceLibrarySitePageBaseItemSchema');

export const ResourceLibrarySitePageItemSchema = ResourceLibrarySitePageBaseItemSchema.extend({
    navigationNode: NavigationNodeSchema.nullable(),
    sections: z
        .array(z.unknown())
        .transform((sections) =>
            transformAndFilterValidItems(ResourceLibrarySitePageSectionSchema, sections)
        ),
})
    .superRefine((item, ctx) => {
        if (item.sections.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'sections must contain at least one entry',
                path: ['sections'],
            });
            return;
        }
    })
    .describe('ResourceLibrarySitePageItemSchema');

export const GetResourceLibrarySitePageItemListingSchema = z
    .object({
        getResourceLibrarySitePageItemListing: z.object({
            edges: z
                .array(
                    z.object({
                        node: z.unknown(),
                    })
                )
                .transform((edges) =>
                    transformAndFilterValidEdges(ResourceLibrarySitePageBaseItemSchema, edges)
                ),
        }),
    })
    .describe('GetResourceLibrarySitePageItemListingSchema');

export const GetResourceLibrarySitePageItemSchema = z
    .object({
        getResourceLibrarySitePageItem: ResourceLibrarySitePageItemSchema,
    })
    .describe('GetResourceLibrarySitePageItemSchema');

export type ResourceLibrarySitePageSectionNode = z.infer<
    typeof ResourceLibrarySitePageSectionSchema
>;
